import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useContentVideoUrl } from '@discovr/features';

import { NO_TIMEOUT, useTimeout } from '../../../../../lib/browser/useTimeout';
import type { MediaController } from './useMediaController';

const MS_PER_SECOND = 1000;
/** Refreshed presigns last 1 h; the initial one 4 h. Swap well inside the shorter window. */
export const ADOPT_FRESH_AFTER_MS = 50 * 60_000;
/** One forced refetch per loaded source; a persistent failure must not loop. */
const MAX_ERROR_REFETCHES = 1;

export interface UseVideoUrlRefreshArgs {
  entryId: string;
  /**
   * The media itself — entry and content version. The content detail presigns
   * a new URL on every fetch; a new URL under the same key is the same video
   * and must not reload the player. A new key starts over with its own URL.
   */
  sourceKey: string;
  enabled: boolean;
  controller: MediaController;
  /** The URL already on the content detail (4 h presign). */
  initialUrl: string;
  /** The media failed again after its one retry with a fresh URL, or no fresh URL could be had. */
  onLoadFailed?: (failure: LoadFailure) => void;
}

export interface LoadFailure {
  /** The source that failed — never assume it is still the current one. */
  sourceKey: string;
  /** Whether this source ever reached `loadedmetadata`: one that never did is refusing the load itself. */
  everLoaded: boolean;
}

export interface VideoUrlRefresh {
  src: string;
  /**
   * Tries a failed video again: a fresh URL when one can be had, otherwise
   * a reload of the current one. The source gets its error retry back too.
   */
  retry: () => void;
}

interface PinnedSource {
  key: string;
  url: string;
}

type Restore = { timeMs: number; paused: boolean };

/** A new source takes the content detail's URL; so does a source that had none yet. */
function adoptsInitialUrl(pinned: PinnedSource, sourceKey: string, initialUrl: string): boolean {
  if (pinned.key !== sourceKey) return true;
  return !pinned.url && !!initialUrl;
}

function refreshDelayFor(enabled: boolean, src: string): number | null {
  if (!enabled || !src) return NO_TIMEOUT;
  return ADOPT_FRESH_AFTER_MS;
}

/**
 * Keeps the presigned video URL alive across a long review. The initial URL
 * plays as-is; a fresh one is fetched on demand — on a timer inside the
 * presign's lifetime, or once after a media `error` (an expired link) — and
 * swapped in with the player put back where it was.
 */
export function useVideoUrlRefresh({ entryId, sourceKey, enabled, controller, initialUrl, onLoadFailed }: UseVideoUrlRefreshArgs): VideoUrlRefresh {
  // Disabled query: nothing is fetched on mount, so the initial URL is not
  // replaced (and the media not re-downloaded) right after first paint.
  const query = useContentVideoUrl(entryId, false);
  const [pinned, setPinned] = useState<PinnedSource>({ key: sourceKey, url: initialUrl });
  if (adoptsInitialUrl(pinned, sourceKey, initialUrl)) {
    setPinned({ key: sourceKey, url: initialUrl });
  }
  const src = pinned.url;

  const srcRef = useRef(src);
  const keyRef = useRef(pinned.key);
  const elementRef = useRef(controller.element);
  const refetchRef = useRef(query.refetch);
  const onLoadFailedRef = useRef(onLoadFailed);
  const restoreRef = useRef<Restore | null>(null);
  const errorRefetchesRef = useRef(0);
  const everLoadedRef = useRef(false);

  // Latest-ref idiom, kept out of the render body: layout (not passive) so
  // the effects below — which read these refs the moment they run — always
  // see this render's values, since layout effects run before passive ones.
  useLayoutEffect(() => {
    srcRef.current = src;
    keyRef.current = pinned.key;
    elementRef.current = controller.element;
    refetchRef.current = query.refetch;
    onLoadFailedRef.current = onLoadFailed;
  });

  // A different entry or content version starts over — never the previous
  // source's playhead, retry or load history. Layout, so it lands before the
  // new source's first media event can.
  useLayoutEffect(() => {
    restoreRef.current = null;
    errorRefetchesRef.current = 0;
    everLoadedRef.current = false;
  }, [pinned.key]);

  /** Resolves whether a fresh URL was swapped in. */
  const adoptFresh = useCallback(async (): Promise<boolean> => {
    const key = keyRef.current;
    const result = await refetchRef.current();
    const fresh = result.data?.url;
    // The source changed while the fetch was out: its URL is not ours to use.
    if (key !== keyRef.current) return false;
    if (!fresh || fresh === srcRef.current) return false;
    const video = elementRef.current;
    restoreRef.current = video ? { timeMs: video.currentTime * MS_PER_SECOND, paused: video.paused } : null;
    setPinned({ key, url: fresh });
    return true;
  }, []);

  const retry = useCallback(() => {
    errorRefetchesRef.current = 0;
    void adoptFresh().then((swapped) => {
      if (!swapped) elementRef.current?.load();
    });
  }, [adoptFresh]);

  const adoptOnTimer = useCallback(() => {
    void adoptFresh();
  }, [adoptFresh]);

  // Restarts on every swap: each fresh URL gets its own window.
  useTimeout(adoptOnTimer, refreshDelayFor(enabled, src), src);

  // Listen on the mounted element itself: the theater re-keys the <video>
  // on a CORS-mode flip, and a listener bound to the old node would be lost.
  useEffect(() => {
    const video = controller.element;
    if (!video) return undefined;
    const onLoaded = () => {
      everLoadedRef.current = true;
      errorRefetchesRef.current = 0;
      const restore = restoreRef.current;
      if (!restore) return;
      restoreRef.current = null;
      video.currentTime = restore.timeMs / MS_PER_SECOND;
      // A browser may refuse to resume without a fresh gesture; the reviewer
      // then presses play themselves, so there is nothing to recover here.
      if (!restore.paused) void video.play().catch(() => undefined);
    };
    const onError = () => {
      const failure: LoadFailure = { sourceKey: keyRef.current, everLoaded: everLoadedRef.current };
      if (!enabled || errorRefetchesRef.current >= MAX_ERROR_REFETCHES) {
        onLoadFailedRef.current?.(failure);
        return;
      }
      errorRefetchesRef.current += 1;
      void adoptFresh().then((swapped) => {
        // A source replaced while its fresh URL was being fetched is nobody's concern any more.
        if (swapped || failure.sourceKey !== keyRef.current) return;
        onLoadFailedRef.current?.(failure);
      });
    };
    video.addEventListener('loadedmetadata', onLoaded);
    video.addEventListener('error', onError);
    return () => {
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('error', onError);
    };
  }, [controller.element, enabled, adoptFresh]);

  return { src, retry };
}
